import { LOCALE_LABELS, SUPPORTED_LOCALES, type Locale } from "@/constants/config";
import { localeFromPathname, swapLocaleInPath } from "@/constants/routes";
import { cn } from "@/lib/utils";
import { usePreferences } from "@/providers/preferences-provider";
import { useLocation, useNavigate } from "react-router-dom";

/**
 * Language switch for the localized public routes (`/en`, `/id`).
 *
 * The URL is the source of truth on public pages, so the active state is derived
 * from it (a stored preference can disagree after a shared link). Changing the
 * language persists the preference *and* navigates to the same path under the other
 * prefix — otherwise the address bar and the rendered copy drift apart.
 */
export const LocaleSwitcher = ({ className }: { className?: string }) => {
  const { locale: preference, setLocale } = usePreferences();
  const navigate = useNavigate();
  const location = useLocation();

  const urlLocale = localeFromPathname(location.pathname);
  const active = urlLocale ?? preference;

  const changeLocale = (next: Locale) => {
    setLocale(next);

    const target = swapLocaleInPath(location.pathname, next);
    if (!target) return; // dashboard routes are not locale-scoped

    navigate(`${target}${location.search}${location.hash}`);
  };

  return (
    <div
      className={cn("inline-flex items-center rounded-lg border border-border p-0.5", className)}
      role="group"
      aria-label="Language"
    >
      {SUPPORTED_LOCALES.map((value) => (
        <button
          key={value}
          type="button"
          aria-pressed={active === value}
          onClick={() => changeLocale(value)}
          className={cn(
            "rounded-md px-2 py-1 text-xs font-medium uppercase transition-colors",
            active === value ? "bg-muted text-foreground" : "text-muted-foreground hover:text-foreground",
          )}
        >
          {LOCALE_LABELS[value]}
        </button>
      ))}
    </div>
  );
};
